import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { Input } from '@ophelos/ui/components/input';
import { Label } from '@ophelos/ui/components/label';
import { Loader } from '@ophelos/ui/components/loader';
import { Separator } from '@ophelos/ui/components/separator';
import { Switch } from '@ophelos/ui/components/switch';
import { Presentation } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { SettingsPageShell } from '@/apps/settings/components/SettingsPageShell';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { isDemoFeatureEnvironment } from '@/lib/hub-environment';
import { useDemoModeAccess } from '@/lib/presentation-mode/access';
import {
  MAX_PRESENTATION_CLIENTS,
  normalizeClientNames,
  setPresentationClientNames,
  setPresentationModeEnabled,
  setPresentationShowInAppSidebar,
  usePresentationMode,
} from '@/lib/presentation-mode/store';

function padNames(names: readonly string[]): string[] {
  return Array.from({ length: MAX_PRESENTATION_CLIENTS }, (_, index) => names[index] ?? '');
}

/** Label and hint on the left, control on the right — the settings page row. */
function SettingRow({
  label,
  description,
  htmlFor,
  children,
}: {
  label: string;
  description?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-2 gap-20 items-center justify-between">
      <div className="stack gap-0.5 min-w-0">
        <Label htmlFor={htmlFor} className="text-base font-medium">
          {label}
        </Label>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="flex items-center justify-end">{children}</div>
    </div>
  );
}

function NotAvailable({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-(--page-main-height) items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="subtle">
              <Presentation />
            </EmptyMedia>
            <EmptyTitle>{title}</EmptyTitle>
            <EmptyDescription>{description}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    </div>
  );
}

export function PresentationModePage() {
  const { allowed, resolved } = useDemoModeAccess();
  if (!isDemoFeatureEnvironment()) {
    return (
      <NotAvailable
        title="Not available in production"
        description="Demo mode serves demo data and is only offered on local and staging Hubs."
      />
    );
  }
  if (!resolved) return <Loader text="Checking access..." />;
  if (!allowed) {
    return (
      <NotAvailable
        title="Not available for client users"
        description="Demo mode is an internal tool for presenting Analytics with demo data and is only offered to operator-wide analytics admins."
      />
    );
  }
  return <PresentationModeSettings />;
}

function PresentationModeSettings() {
  const { enabled, clientNames, showInAppSidebar } = usePresentationMode();
  const [draftNames, setDraftNames] = useState(() => padNames(clientNames));

  const normalizedDraft = normalizeClientNames(draftNames);
  const hasChanges = JSON.stringify(normalizedDraft) !== JSON.stringify(clientNames);

  const handleToggle = (next: boolean) => {
    setPresentationModeEnabled(next);
    toast.success(next ? 'Demo mode on — Analytics shows demo data' : 'Demo mode off — Analytics shows live data');
  };

  const handleSave = () => {
    setPresentationClientNames(draftNames);
    setDraftNames(padNames(normalizedDraft));
    toast.success('Demo clients saved');
  };

  return (
    <SettingsPageShell
      title="Demo mode"
      description="Fill the Analytics app with realistic demo data for live demos."
      hasChanges={hasChanges}
      onSave={handleSave}
      onReset={() => setDraftNames(padNames(clientNames))}
      maxWidth="max-w-4xl"
      scopePreview={false}
    >
      <div className="space-y-10">
        <Card headerPosition="outside">
          <CardHeader>
            <CardTitle className="text-lg">Demo data</CardTitle>
            <CardDescription>
              Dashboard, Performance, Engagement and Customer Service read from generated fixtures instead of the API.
            </CardDescription>
          </CardHeader>
          <CardContent className="py-3">
            <SettingRow
              label="Demo mode"
              description="Also available as the switch next to Demo mode in the sidebar."
              htmlFor="demo-mode-switch"
            >
              <Switch id="demo-mode-switch" checked={enabled} onCheckedChange={handleToggle} />
            </SettingRow>
          </CardContent>
          <Separator className="bg-border" />
          <CardContent className="py-3">
            <SettingRow
              label="Switch in the Analytics sidebar"
              description="Show the quick toggle inside the Analytics app as well as in the Hub sidebar."
              htmlFor="demo-mode-app-sidebar-switch"
            >
              <Switch id="demo-mode-app-sidebar-switch" checked={showInAppSidebar} onCheckedChange={setPresentationShowInAppSidebar} />
            </SettingRow>
          </CardContent>
        </Card>

        <Card headerPosition="outside">
          <CardHeader>
            <CardTitle className="text-lg">Demo clients</CardTitle>
            <CardDescription>
              Up to {MAX_PRESENTATION_CLIENTS} client names shown across the demo. Leave a row blank to use fewer.
            </CardDescription>
          </CardHeader>
          {draftNames.map((name, index) => {
            const id = `presentation-client-${index + 1}`;
            return (
              <div key={id}>
                {index > 0 && <Separator className="bg-border" />}
                <CardContent className="py-3">
                  <SettingRow label={`Client ${index + 1}`} htmlFor={id}>
                    <Input
                      id={id}
                      size="sm"
                      value={name}
                      maxLength={60}
                      placeholder={index === 0 ? 'Required' : 'Optional'}
                      className="w-full"
                      onChange={(event) => {
                        const next = [...draftNames];
                        next[index] = event.target.value;
                        setDraftNames(next);
                      }}
                    />
                  </SettingRow>
                </CardContent>
              </div>
            );
          })}
        </Card>
      </div>
    </SettingsPageShell>
  );
}
